import { lt } from './localized'
import type {
  HideoutTierId,
  LocalizedText,
  Needs,
  RecipeIngredient,
  ShelterHideoutPersisted,
  Survivor,
  WorkstationId,
  WorkstationRuntimeState,
  WorkstationToolDef,
  WorkstationUpgradeTierGate,
} from './types'
import { getSkillLevel } from './skills'
import { WORKSTATIONS } from './workstations'

export const PASSIVE_NEED_REGEN_PER_MIN_BY_LEVEL: Record<number, number> = {
  1: 2,
  2: 5,
  3: 10,
}

const RESTORE_NEED_STATION_TO_NEED: Partial<Record<WorkstationId, keyof Needs>> = {
  kitchen: 'hunger',
  well: 'thirst',
}

export function getPassiveNeedRegenPerMinute(level: number): number {
  const normalized = Math.max(1, Math.floor(level))
  return PASSIVE_NEED_REGEN_PER_MIN_BY_LEVEL[normalized] ?? PASSIVE_NEED_REGEN_PER_MIN_BY_LEVEL[3]
}

export function getRestoreNeedForWorkstation(
  stationId: WorkstationId,
): keyof Needs | null {
  return RESTORE_NEED_STATION_TO_NEED[stationId] ?? null
}

export const HIDEOUT_TIER_ORDER: HideoutTierId[] = [
  'forest_hideout',
  'boarded_workplace',
  'train_depot',
  'utrecht_centrum',
]

export const HIDEOUT_TIER_META: Record<
  HideoutTierId,
  { name: LocalizedText; blurb: LocalizedText; survivorCap: number }
> = {
  forest_hideout: {
    name: lt('Boskampement — Vinkel', 'Forest hideout'),
    blurb: lt(
      'Een rotkampement bij het dorp. Weinig ruimte, maar het leeft.',
      'A rough camp near the village. Little space, but it’s alive.',
    ),
    survivorCap: 4,
  },
  boarded_workplace: {
    name: lt('Afgeschermde werkplaats', 'Boarded-up workplace'),
    blurb: lt(
      'Meer muur dan raam. Genoeg ruimte om op te bouwen.',
      'More wall than window. Enough room to build on.',
    ),
    survivorCap: 5,
  },
  train_depot: {
    name: lt('Verlaten treindepot — Den Bosch', 'Abandoned train depot — ’s-Hertogenbosch'),
    blurb: lt(
      'Sporen, magazijnen en ruimte voor echte logistiek.',
      'Tracks, warehouses, and room for real logistics.',
    ),
    survivorCap: 6,
  },
  utrecht_centrum: {
    name: lt('Centrumhub — Utrecht', 'City hub — Utrecht'),
    blurb: lt(
      'Grachtenring en stationsbuurt — ruimte voor grotere operaties.',
      'Canal ring and station quarter—room for bigger operations.',
    ),
    survivorCap: 8,
  },
}

/** Max station level allowed at each hideout tier (per station). */
export const STATION_LEVEL_CAP: Record<
  HideoutTierId,
  Record<WorkstationId, number>
> = {
  forest_hideout: {
    base_station: 1,
    stash: 1,
    kitchen: 1,
    well: 1,
    infirmary: 1,
    lodging: 1,
    broadcast_station: 1,
    workshop: 1,
    garage: 1,
    training_yard: 1,
    computer_lab: 1,
    market_stall: 1,
  },
  boarded_workplace: {
    base_station: 2,
    stash: 2,
    kitchen: 2,
    well: 2,
    infirmary: 2,
    lodging: 2,
    broadcast_station: 2,
    workshop: 2,
    garage: 2,
    training_yard: 2,
    computer_lab: 2,
    market_stall: 2,
  },
  train_depot: {
    base_station: 3,
    stash: 3,
    kitchen: 3,
    well: 3,
    infirmary: 3,
    lodging: 3,
    broadcast_station: 3,
    workshop: 3,
    garage: 3,
    training_yard: 3,
    computer_lab: 3,
    market_stall: 3,
  },
  utrecht_centrum: {
    base_station: 3,
    stash: 3,
    kitchen: 3,
    well: 3,
    infirmary: 3,
    lodging: 3,
    broadcast_station: 3,
    workshop: 3,
    garage: 3,
    training_yard: 3,
    computer_lab: 3,
    market_stall: 3,
  },
}

/**
 * Upgrade station from (nextLevel - 1) → nextLevel.
 * Keys are the target level after upgrade.
 */
export const WORKSTATION_UPGRADES: Partial<
  Record<WorkstationId, Partial<Record<number, WorkstationUpgradeTierGate>>>
> = {
  kitchen: {
    2: {
      minHideoutTier: 'boarded_workplace',
      costs: [
        { defId: 'scrap-rusty', quantity: 4 },
        { category: 'food', quantity: 1 },
      ],
      benefit: lt(
        'Grotere kookplaat en betere pannen — meer herstel per maaltijd.',
        'Bigger burner and better pans — more restoration per meal.',
      ),
    },
    3: {
      minHideoutTier: 'train_depot',
      costs: [
        { defId: 'scrap-electronics', quantity: 2 },
        { defId: 'scrap-rusty', quantity: 6 },
      ],
      benefit: lt(
        'Voorlopige keukenunits — recepten zijn sneller klaar.',
        'Makeshift kitchen units — recipes finish faster.',
      ),
    },
  },
  well: {
    2: {
      minHideoutTier: 'boarded_workplace',
      costs: [
        { defId: 'water-rain-jerrycan', quantity: 1 },
        { defId: 'scrap-rusty', quantity: 3 },
      ],
      benefit: lt(
        'Extra filters — zuiveren wordt efficiënter.',
        'Extra filters — purification becomes more efficient.',
      ),
    },
    3: {
      minHideoutTier: 'train_depot',
      costs: [
        { defId: 'scrap-electronics', quantity: 1 },
        { defId: 'scrap-rusty', quantity: 4 },
      ],
      benefit: lt(
        'Voorraadtanks — meer output per batch.',
        'Buffer tanks — more output per batch.',
      ),
    },
  },
  infirmary: {
    2: {
      minHideoutTier: 'boarded_workplace',
      costs: [
        { category: 'meds', quantity: 2 },
        { defId: 'scrap-rusty', quantity: 2 },
      ],
      benefit: lt(
        'Sterielere werkplek — sneller verwondingen stabiliseren.',
        'Cleaner setup — faster stabilization of injuries.',
      ),
    },
    3: {
      minHideoutTier: 'train_depot',
      costs: [
        { defId: 'meds-bandages', quantity: 2 },
        { defId: 'scrap-toolkit', quantity: 1 },
      ],
      benefit: lt(
        'Kleine chirurgiehoek — efficiëntere nazorg.',
        'Minor surgery corner — more efficient wound care.',
      ),
    },
  },
  lodging: {
    2: {
      minHideoutTier: 'boarded_workplace',
      costs: [
        { defId: 'scrap-plank', quantity: 8 },
        { defId: 'scrap-rusty', quantity: 6 },
      ],
      benefit: lt(
        'Extra slaapzaal — meer rustplaatsen en ruimte voor meer mensen.',
        'Extra dorm — more beds and room for more survivors.',
      ),
    },
    3: {
      minHideoutTier: 'train_depot',
      costs: [
        { defId: 'scrap-plank', quantity: 12 },
        { defId: 'scrap-toolkit', quantity: 2 },
        { defId: 'scrap-rusty', quantity: 10 },
      ],
      benefit: lt(
        'Echte slaapkamers — maximale capaciteit en comfort.',
        'Proper quarters — maximum housing capacity.',
      ),
    },
  },
  broadcast_station: {
    2: {
      minHideoutTier: 'boarded_workplace',
      costs: [
        { defId: 'scrap-electronics', quantity: 2 },
        { defId: 'scrap-wire', quantity: 6 },
      ],
      benefit: lt(
        'Betere antenne — vaker contact via de radio.',
        'Better antenna — radio picks up contacts more often.',
      ),
    },
    3: {
      minHideoutTier: 'train_depot',
      costs: [
        { defId: 'scrap-electronics', quantity: 4 },
        { defId: 'scrap-toolkit', quantity: 1 },
      ],
      benefit: lt(
        'Versterker — zendbereik en stabiliteit omhoog.',
        'Amplifier — boosted signal clarity.',
      ),
    },
  },
  garage: {
    2: {
      minHideoutTier: 'boarded_workplace',
      costs: [
        { defId: 'scrap-rusty', quantity: 8 },
        { defId: 'scrap-plank', quantity: 4 },
        { defId: 'scrap-toolkit', quantity: 1 },
      ],
      benefit: lt(
        'Uitbreiding met twee extra plekken — grotere voertuigen passen nu.',
        'Extended bay with two more slots — larger vehicles now fit.',
      ),
    },
    3: {
      minHideoutTier: 'train_depot',
      costs: [
        { defId: 'scrap-rusty', quantity: 14 },
        { defId: 'scrap-plank', quantity: 8 },
        { defId: 'scrap-electronics', quantity: 2 },
      ],
      benefit: lt(
        'Diepe garagebaai — nog eens twee kleine slots erbij.',
        'Deep garage bay — two more small-equivalent slots.',
      ),
    },
  },
  workshop: {
    2: {
      minHideoutTier: 'boarded_workplace',
      costs: [
        { defId: 'scrap-rusty', quantity: 5 },
        { defId: 'scrap-plank', quantity: 3 },
        { defId: 'scrap-toolkit', quantity: 1 },
      ],
      benefit: lt(
        'Werkbank en bankschroef — betere recepten en sneller klussen.',
        'Workbench and vice — better recipes and faster crafts.',
      ),
    },
    3: {
      minHideoutTier: 'train_depot',
      costs: [
        { defId: 'scrap-electronics', quantity: 3 },
        { defId: 'scrap-rusty', quantity: 8 },
        { defId: 'scrap-plank', quantity: 4 },
      ],
      benefit: lt(
        'Elektrische gereedschappen — nog snellere productie.',
        'Power tools — even faster production.',
      ),
    },
  },
  stash: {
    2: {
      minHideoutTier: 'boarded_workplace',
      costs: [
        { defId: 'scrap-rusty', quantity: 6 },
        { defId: 'scrap-plank', quantity: 4 },
      ],
      benefit: lt(
        'Geïmproviseerde kast — meer stapelplaatsen.',
        'Improvised shelving — more stack slots.',
      ),
    },
    3: {
      minHideoutTier: 'train_depot',
      costs: [
        { defId: 'scrap-toolkit', quantity: 2 },
        { defId: 'scrap-rusty', quantity: 10 },
        { defId: 'scrap-plank', quantity: 6 },
      ],
      benefit: lt(
        'Geëtiketteerde rekken — nog meer opslag.',
        'Labelled racks — even more storage.',
      ),
    },
  },
  base_station: {
    2: {
      minHideoutTier: 'boarded_workplace',
      costs: [
        { defId: 'scrap-rusty', quantity: 4 },
        { category: 'scrap', quantity: 2 },
      ],
      benefit: lt(
        'Kaartentafel en laden — beter overzicht voor missies.',
        'Map table and drawers — better mission overview.',
      ),
    },
    3: {
      minHideoutTier: 'train_depot',
      costs: [
        { defId: 'scrap-electronics', quantity: 4 },
        { defId: 'scrap-plank', quantity: 6 },
        { defId: 'scrap-toolkit', quantity: 1 },
      ],
      benefit: lt(
        'Kaartenwand en verbinding naar zendstation — commandopost.',
        'Wall maps and tie-ins for broadcasting — a proper command post.',
      ),
    },
  },
}

/** First craft (level 0 → 1) for stations that start unbuilt in a new game. */
export const WORKSTATION_INITIAL_BUILD: Partial<
  Record<WorkstationId, WorkstationUpgradeTierGate>
> = {
  kitchen: {
    minHideoutTier: 'forest_hideout',
    costs: [
      { defId: 'scrap-rusty', quantity: 2 },
      { category: 'food', quantity: 3 },
    ],
    benefit: lt(
      'Kampvuur en kookplek — bereid voedsel voor je team.',
      'Campfire and cooking spot — prepare food for your team.',
    ),
  },
  well: {
    minHideoutTier: 'forest_hideout',
    costs: [
      { defId: 'scrap-rusty', quantity: 3 },
      { defId: 'water-bottle-half', quantity: 3 },
    ],
    benefit: lt(
      'Wateropslag en filtering — dorst lessen.',
      'Water storage and filtering — ease thirst.',
    ),
  },
  infirmary: {
    minHideoutTier: 'forest_hideout',
    costs: [
      { defId: 'scrap-rusty', quantity: 4 },
      { category: 'meds', quantity: 2 },
    ],
    benefit: lt(
      'Basisverzorging — stabiliseer verwondingen.',
      'Basic care — stabilize injuries.',
    ),
  },
  lodging: {
    minHideoutTier: 'forest_hideout',
    costs: [
      { defId: 'scrap-plank', quantity: 6 },
      { defId: 'scrap-rusty', quantity: 5 },
    ],
    benefit: lt(
      'Trek onder dak — meer plaats voor mensen en rust voor energie.',
      'Shelter space — room for more people and rest to recover energy.',
    ),
  },
  broadcast_station: {
    minHideoutTier: 'forest_hideout',
    costs: [
      { defId: 'scrap-radio', quantity: 1 },
      { defId: 'scrap-electronics', quantity: 1 },
      { defId: 'scrap-wire', quantity: 4 },
    ],
    benefit: lt(
      'Zendmast improviseren — luister naar andere groepen.',
      'Improvised mast — reach other survivor groups by radio.',
    ),
  },
  workshop: {
    minHideoutTier: 'forest_hideout',
    costs: [
      { defId: 'scrap-rusty', quantity: 12 },
      { defId: 'scrap-plank', quantity: 6 },
    ],
    benefit: lt(
      'Werkbank — maak gereedschap en onderdelen.',
      'Workbench — craft tools and parts.',
    ),
  },
  garage: {
    minHideoutTier: 'forest_hideout',
    costs: [
      { defId: 'scrap-rusty', quantity: 10 },
      { defId: 'scrap-plank', quantity: 5 },
    ],
    benefit: lt(
      'Fietsrek voor twee kleine voertuigen — fietsen passen hier.',
      'Bike rack for two small vehicles — bikes fit here.',
    ),
  },
  training_yard: {
    minHideoutTier: 'utrecht_centrum',
    costs: [
      { defId: 'scrap-plank', quantity: 10 },
      { defId: 'scrap-rusty', quantity: 14 },
    ],
    benefit: lt(
      'Ring en rekken — survivors trainen uithouding en reflex.',
      'Ring and racks—survivors train endurance and reflex.',
    ),
  },
  computer_lab: {
    minHideoutTier: 'utrecht_centrum',
    costs: [
      { defId: 'scrap-electronics', quantity: 6 },
      { defId: 'scrap-wire', quantity: 8 },
      { defId: 'laptop-salvage', quantity: 1 },
    ],
    benefit: lt(
      'Terminalbank — hack jobs voor digitale buit en euro’s.',
      'Terminal bank—hack jobs for digital hauls and euros.',
    ),
  },
  market_stall: {
    minHideoutTier: 'utrecht_centrum',
    costs: [
      { defId: 'scrap-plank', quantity: 8 },
      { defId: 'scrap-rusty', quantity: 12 },
      { category: 'trinket', quantity: 2 },
    ],
    benefit: lt(
      'Handelsplein — stuur een Trader op missies voor Stash €.',
      'Trading floor—send a Trader out for stash credits.',
    ),
  },
}

export function getInitialBuildGate(
  stationId: WorkstationId,
): WorkstationUpgradeTierGate | null {
  return WORKSTATION_INITIAL_BUILD[stationId] ?? null
}

export const WORKSTATION_TOOLS: Record<string, WorkstationToolDef> = {
  'kitchen-pot': {
    id: 'kitchen-pot',
    workstationId: 'kitchen',
    name: lt('Kookpot', 'Cooking pot'),
    blurb: lt(
      'Stoofpotten warmen beter — meer herstel bij het voeden.',
      'Stews heat better — more restoration when feeding.',
    ),
    glyph: '🫕',
    requiredStationLevel: 1,
    costs: [{ defId: 'tool-cooking-pot', quantity: 1 }],
    effects: { needRestoreBonus: 10 },
  },
  'workshop-clamp': {
    id: 'workshop-clamp',
    workstationId: 'workshop',
    name: lt('Lijmklem & haak', 'Clamp & hook rack'),
    blurb: lt(
      'Houdt onderdelen vast — iets sneller klussen.',
      'Holds parts steady — slightly faster crafting.',
    ),
    glyph: '🔩',
    requiredStationLevel: 1,
    costs: [
      { defId: 'scrap-rusty', quantity: 4 },
      { defId: 'scrap-plank', quantity: 2 },
    ],
    effects: { craftDurationMultiplier: 0.9 },
  },
  'garage-basic-kit': {
    id: 'garage-basic-kit',
    workstationId: 'garage',
    name: lt('Reparatieset', 'Repair kit'),
    blurb: lt(
      'Een kleine voorraad onderdelen — sneller sleutelen terwijl voertuigen stilstaan.',
      'A small parts stash — faster tinkering while vehicles sit idle.',
    ),
    glyph: '🔧',
    requiredStationLevel: 1,
    costs: [
      { defId: 'scrap-rusty', quantity: 6 },
      { defId: 'scrap-plank', quantity: 2 },
    ],
    effects: { vehicleRepairPerMinuteBonus: 1 },
  },
  'garage-lift': {
    id: 'garage-lift',
    workstationId: 'garage',
    name: lt('Garageheffer', 'Garage lift'),
    blurb: lt(
      'Een hefinrichting voor voertuigen — meer tijd voor serieuze reparaties en minder bijslijtage.',
      'A lift rig for vehicles — more time for real repairs and less wear.',
    ),
    glyph: '🛠️',
    requiredStationLevel: 2,
    costs: [
      { defId: 'scrap-rusty', quantity: 12 },
      { defId: 'scrap-electronics', quantity: 2 },
      { defId: 'scrap-plank', quantity: 4 },
    ],
    effects: { vehicleRepairPerMinuteBonus: 2 },
  },
}

export type RelocationDef = {
  to: HideoutTierId
  minProgress: number
  costs: RecipeIngredient[]
  title: LocalizedText
  blurb: LocalizedText
}

export const RELOCATION_FROM_TIER: Partial<Record<HideoutTierId, RelocationDef>> = {
  forest_hideout: {
    to: 'boarded_workplace',
    minProgress: 3,
    costs: [
      { defId: 'scrap-rusty', quantity: 8 },
      { defId: 'scrap-plank', quantity: 6 },
    ],
    title: lt('Verhuizen naar de werkplaats', 'Move to the workplace'),
    blurb: lt(
      'Het bos is te zichtbaar geworden. Een afgeschermde ruimte in het dorp wacht.',
      'The forest became too exposed. A boarded-up space in the village awaits.',
    ),
  },
  boarded_workplace: {
    to: 'train_depot',
    minProgress: 12,
    costs: [
      { defId: 'scrap-rusty', quantity: 15 },
      { defId: 'scrap-plank', quantity: 10 },
      { defId: 'scrap-electronics', quantity: 3 },
    ],
    title: lt('Naar Den Bosch — treindepot', 'To ’s-Hertogenbosch — train depot'),
    blurb: lt(
      'De stad roept: grotere hub, meer upgrades, meer risico.',
      'The city calls: larger hub, more upgrades, more risk.',
    ),
  },
  train_depot: {
    to: 'utrecht_centrum',
    minProgress: 24,
    costs: [
      { defId: 'scrap-rusty', quantity: 22 },
      { defId: 'scrap-plank', quantity: 14 },
      { defId: 'scrap-electronics', quantity: 6 },
      { defId: 'scrap-wire', quantity: 10 },
    ],
    title: lt('Naar Utrecht — centrumhub', 'To Utrecht — city hub'),
    blurb: lt(
      'Noordelijke corridor open: een nieuwe basis tussen grachten.',
      'Northern corridor open—a new base between the canals.',
    ),
  },
}

export function hideoutTierIndex(tier: HideoutTierId): number {
  return HIDEOUT_TIER_ORDER.indexOf(tier)
}

/** Active expeditions allowed at once (starts at 2 so two survivors can run different trips). */
export function maxConcurrentExpeditions(hideoutTierId: HideoutTierId): number {
  return hideoutTierIndex(hideoutTierId) + 2
}

export function meetsHideoutTierMinimum(
  current: HideoutTierId,
  required: HideoutTierId,
): boolean {
  return hideoutTierIndex(current) >= hideoutTierIndex(required)
}

const emptyComputerLabState = () =>
  ({
    computerLabSlots: [null, null, null, null] as [
      string | null,
      string | null,
      string | null,
      string | null,
    ],
    computerLabWorkers: [null, null, null, null] as [
      string | null,
      string | null,
      string | null,
      string | null,
    ],
    computerLabJobEndsAt: [null, null, null, null] as [
      number | null,
      number | null,
      number | null,
      number | null,
    ],
  }) as const

function workstationRuntimeBase(
  built: boolean,
  level: number,
): WorkstationRuntimeState {
  return {
    built,
    level,
    assignedSurvivorId: null,
    installedToolIds: [],
    generatedItems: [],
    generationAnchorAt: null,
    needRegenAnchorAt: null,
    gemHealAvailableAt: null,
    garageRepairAnchorAt: null,
    lodgingOccupantIds: [],
    broadcastRecruitOffers: [],
    trainingYardSessionEndsAt: null,
    ...emptyComputerLabState(),
  }
}

/** Legacy saves and migrations: every station built at level 1. */
export function legacyAllBuiltWorkstationStates(): Record<
  WorkstationId,
  WorkstationRuntimeState
> {
  const empty = (): WorkstationRuntimeState => workstationRuntimeBase(true, 1)
  const unbuilt = (): WorkstationRuntimeState => workstationRuntimeBase(false, 0)
  return {
    base_station: empty(),
    stash: empty(),
    kitchen: empty(),
    well: empty(),
    infirmary: empty(),
    lodging: unbuilt(),
    broadcast_station: unbuilt(),
    workshop: empty(),
    garage: unbuilt(),
    training_yard: unbuilt(),
    computer_lab: unbuilt(),
    market_stall: unbuilt(),
  }
}

/** New game: only base station + stash; other stations must be built (0→1). */
export function newGameWorkstationStates(): Record<
  WorkstationId,
  WorkstationRuntimeState
> {
  const built = (): WorkstationRuntimeState => workstationRuntimeBase(true, 1)
  const unbuilt = (): WorkstationRuntimeState => workstationRuntimeBase(false, 0)
  return {
    base_station: built(),
    stash: built(),
    kitchen: unbuilt(),
    well: unbuilt(),
    infirmary: unbuilt(),
    lodging: unbuilt(),
    broadcast_station: unbuilt(),
    workshop: unbuilt(),
    garage: unbuilt(),
    training_yard: unbuilt(),
    computer_lab: unbuilt(),
    market_stall: unbuilt(),
  }
}

/** @deprecated Prefer `legacyAllBuiltWorkstationStates` or `newGameWorkstationStates`. */
export function defaultWorkstationStates(): Record<
  WorkstationId,
  WorkstationRuntimeState
> {
  return legacyAllBuiltWorkstationStates()
}

/**
 * Overlay persisted workstation state onto defaults.
 * Use `legacy` when migrating old saves or when partial is undefined (all-built baseline).
 * Use `newGame` when merging a partial blob onto minimal camp defaults.
 */
export function mergeWorkstationStates(
  partial: Partial<Record<WorkstationId, WorkstationRuntimeState>> | undefined,
  base: 'newGame' | 'legacy' = 'legacy',
): Record<WorkstationId, WorkstationRuntimeState> {
  const defaults =
    base === 'newGame'
      ? newGameWorkstationStates()
      : legacyAllBuiltWorkstationStates()
  if (!partial) return defaults
  const ids = Object.keys(defaults) as WorkstationId[]
  const out = { ...defaults }
  for (const id of ids) {
    const p = partial[id]
    if (!p) continue
    const built = p.built ?? defaults[id].built
    const level = built ? Math.max(1, p.level ?? 1) : 0
    const defLodging =
      Array.isArray(p.lodgingOccupantIds) &&
      p.lodgingOccupantIds.every((x) => typeof x === 'string')
        ? p.lodgingOccupantIds
        : defaults[id].lodgingOccupantIds
    const defBroadcast = Array.isArray(p.broadcastRecruitOffers)
      ? (p.broadcastRecruitOffers as unknown[]).filter(
          (x): x is Survivor =>
            typeof x === 'object' &&
            x !== null &&
            typeof (x as Survivor).id === 'string',
        )
      : defaults[id].broadcastRecruitOffers

    const rawGarageRepairAnchorAt = (p as Partial<WorkstationRuntimeState>).garageRepairAnchorAt
    out[id] = {
      built,
      level,
      assignedSurvivorId: p.assignedSurvivorId ?? null,
      installedToolIds: Array.isArray(p.installedToolIds)
        ? p.installedToolIds
        : [],
      generatedItems: Array.isArray(p.generatedItems)
        ? p.generatedItems
            .filter((s) => s && typeof s.defId === 'string' && typeof s.quantity === 'number')
            .map((s) => ({ defId: s.defId, quantity: Math.max(0, Math.floor(s.quantity)) }))
            .filter((s) => s.quantity > 0)
        : [],
      generationAnchorAt:
        typeof p.generationAnchorAt === 'number' && Number.isFinite(p.generationAnchorAt)
          ? p.generationAnchorAt
          : null,
      needRegenAnchorAt:
        typeof p.needRegenAnchorAt === 'number' && Number.isFinite(p.needRegenAnchorAt)
          ? p.needRegenAnchorAt
          : null,
      gemHealAvailableAt:
        typeof p.gemHealAvailableAt === 'number' && Number.isFinite(p.gemHealAvailableAt)
          ? p.gemHealAvailableAt
          : null,
      garageRepairAnchorAt:
        typeof rawGarageRepairAnchorAt === 'number' &&
        Number.isFinite(rawGarageRepairAnchorAt)
          ? rawGarageRepairAnchorAt
          : null,
      lodgingOccupantIds: defLodging,
      broadcastRecruitOffers: defBroadcast,
      computerLabSlots:
        Array.isArray((p as WorkstationRuntimeState).computerLabSlots) &&
        (p as WorkstationRuntimeState).computerLabSlots!.length === 4
          ? ((p as WorkstationRuntimeState).computerLabSlots as [
              string | null,
              string | null,
              string | null,
              string | null,
            ])
          : defaults[id].computerLabSlots,
      computerLabWorkers:
        Array.isArray((p as WorkstationRuntimeState).computerLabWorkers) &&
        (p as WorkstationRuntimeState).computerLabWorkers!.length === 4
          ? ((p as WorkstationRuntimeState).computerLabWorkers as [
              string | null,
              string | null,
              string | null,
              string | null,
            ])
          : defaults[id].computerLabWorkers,
      computerLabJobEndsAt:
        Array.isArray((p as WorkstationRuntimeState).computerLabJobEndsAt) &&
        (p as WorkstationRuntimeState).computerLabJobEndsAt!.length === 4
          ? ((p as WorkstationRuntimeState).computerLabJobEndsAt as [
              number | null,
              number | null,
              number | null,
              number | null,
            ])
          : defaults[id].computerLabJobEndsAt,
      tradingMissionEndsAt:
        typeof (p as WorkstationRuntimeState).tradingMissionEndsAt === 'number'
          ? (p as WorkstationRuntimeState).tradingMissionEndsAt!
          : defaults[id].tradingMissionEndsAt ?? null,
      tradingMissionSurvivorId:
        typeof (p as WorkstationRuntimeState).tradingMissionSurvivorId === 'string' ||
        (p as WorkstationRuntimeState).tradingMissionSurvivorId === null
          ? (p as WorkstationRuntimeState).tradingMissionSurvivorId ?? null
          : defaults[id].tradingMissionSurvivorId ?? null,
      trainingYardSessionEndsAt:
        typeof (p as WorkstationRuntimeState).trainingYardSessionEndsAt === 'number' &&
        Number.isFinite((p as WorkstationRuntimeState).trainingYardSessionEndsAt)
          ? (p as WorkstationRuntimeState).trainingYardSessionEndsAt!
          : (p as WorkstationRuntimeState).trainingYardSessionEndsAt === null
            ? null
            : defaults[id].trainingYardSessionEndsAt ?? null,
    }
  }
  return out
}

export function getStationLevelCap(
  hideoutTier: HideoutTierId,
  stationId: WorkstationId,
): number {
  return STATION_LEVEL_CAP[hideoutTier][stationId] ?? 1
}

export function getUpgradeGateForNextLevel(
  stationId: WorkstationId,
  currentLevel: number,
): WorkstationUpgradeTierGate | null {
  const targetLevel = currentLevel + 1
  return WORKSTATION_UPGRADES[stationId]?.[targetLevel] ?? null
}

export function canReachStationLevel(
  hideoutTier: HideoutTierId,
  stationId: WorkstationId,
  targetLevel: number,
): boolean {
  return targetLevel <= getStationLevelCap(hideoutTier, stationId)
}

/** Stack soft-cap for stash tab (tiers + stash station level). */
export function getStashSoftCap(
  hideoutTier: HideoutTierId,
  stashLevel: number,
): number {
  const tierBonus = hideoutTierIndex(hideoutTier) * 8
  return 40 + tierBonus + stashLevel * 6
}

export function normalizeShelterHideout(
  raw: Partial<ShelterHideoutPersisted> | null | undefined,
): { tierId: HideoutTierId; workstations: Record<WorkstationId, WorkstationRuntimeState> } {
  const tierId =
    raw?.tierId &&
    HIDEOUT_TIER_ORDER.includes(raw.tierId as HideoutTierId)
      ? (raw.tierId as HideoutTierId)
      : 'forest_hideout'
  const w = raw?.workstations
  const hasWorkstationBlob =
    w &&
    typeof w === 'object' &&
    Object.keys(w as object).length > 0
  return {
    tierId,
    workstations: hasWorkstationBlob
      ? mergeWorkstationStates(
          w as Partial<Record<WorkstationId, WorkstationRuntimeState>>,
          'newGame',
        )
      : legacyAllBuiltWorkstationStates(),
  }
}

export function serializeShelterHideout(
  tierId: HideoutTierId,
  workstations: Record<WorkstationId, WorkstationRuntimeState>,
): ShelterHideoutPersisted {
  return { tierId, workstations }
}

export function computeNeedRestoreAmount(
  workstationId: WorkstationId,
  base: number,
  ws: WorkstationRuntimeState,
  assignedSurvivor: Survivor | undefined,
): number {
  let amount = base + Math.max(0, ws.level - 1) * 3
  for (const tid of ws.installedToolIds) {
    const t = WORKSTATION_TOOLS[tid]
    if (t?.workstationId === workstationId) {
      amount += t.effects.needRestoreBonus ?? 0
    }
  }
  if (assignedSurvivor) {
    if (workstationId === 'kitchen') {
      const cooking = getSkillLevel(assignedSurvivor, 'cooking')
      amount += Math.min(6, Math.floor(cooking * 0.6))
    }
    if (workstationId === 'kitchen' && assignedSurvivor.role === 'Cook') {
      amount += 7
    }
    if (workstationId === 'well' && assignedSurvivor.role === 'Scavenger') {
      amount += 5
    }
  }
  return amount
}

/** Matches stash consumption in `useGameStore.useItemFromStash`. */
export const STASH_RESTORE_MULTIPLIER = 0.65

/** Baseline injury-heal preview when using meds from stash (no workstation scaling). */
export const STASH_MEDS_INJURY_PREVIEW = 25

const EMPTY_WORKSTATION_FOR_STASH: WorkstationRuntimeState = {
  built: false,
  level: 0,
  assignedSurvivorId: null,
  installedToolIds: [],
  generatedItems: [],
  generationAnchorAt: null,
  needRegenAnchorAt: null,
  gemHealAvailableAt: null,
  garageRepairAnchorAt: null,
  lodgingOccupantIds: [],
  broadcastRecruitOffers: [],
}

/** Preview need points restored when using food/water/meds from stash (injury heal uses a different path). */
export function computeStashNeedRestorePreview(
  category: 'food' | 'water' | 'meds',
  workstations: Record<WorkstationId, WorkstationRuntimeState>,
  survivors: Survivor[],
): number {
  if (category === 'meds') {
    return STASH_MEDS_INJURY_PREVIEW
  }
  const sourceWorkstationId: WorkstationId =
    category === 'food' ? 'kitchen' : 'well'
  const sourceDef = WORKSTATIONS[sourceWorkstationId]
  const sourceWs = workstations[sourceWorkstationId]
  if (sourceDef.kind !== 'restoreNeed' || sourceDef.needRestore === undefined) {
    return 1
  }
  const assignedSurvivor = sourceWs?.assignedSurvivorId
    ? survivors.find((s) => s.id === sourceWs.assignedSurvivorId)
    : undefined
  const baselineRestore = computeNeedRestoreAmount(
    sourceWorkstationId,
    sourceDef.needRestore,
    sourceWs ?? EMPTY_WORKSTATION_FOR_STASH,
    assignedSurvivor,
  )
  return Math.max(1, Math.floor(baselineRestore * STASH_RESTORE_MULTIPLIER))
}

export function computeCraftDurationMs(
  workstationId: WorkstationId,
  baseMs: number,
  ws: WorkstationRuntimeState,
): number {
  let ms = baseMs * Math.pow(0.92, Math.max(0, ws.level - 1))
  for (const tid of ws.installedToolIds) {
    const t = WORKSTATION_TOOLS[tid]
    if (t?.workstationId === workstationId && t.effects.craftDurationMultiplier) {
      ms *= t.effects.craftDurationMultiplier
    }
  }
  return Math.max(0, Math.round(ms))
}
